import { Injectable, inject } from '@angular/core';

import { StreamProvider, StreamType } from '@cds/betting-offer/add-ons';
import { AnimationProviderService } from '@frontend/sports/betting-offer/feature/animation';
import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { EventModel, getBackgroundColor } from '@frontend/sports/betting-offer/feature/model';
import { ImgConfigurationData, MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { NativeAppService } from '@frontend/vanilla/core';

@Injectable({ providedIn: 'root' })
export class EventDetailsHelperService {
    sportTypes: { [key: number]: string } = {
        4: 'soccer',
        5: 'tennis',
        7: 'basketball',
    };

    private readonly mediaConfig = inject(MediaConfig);
    private readonly animationProvider = inject(AnimationProviderService);
    private readonly imgConfig = inject(ImgConfigurationData);
    private readonly nativeApp = inject(NativeAppService);
    private readonly eventMediaHelperService = inject(EventMediaHelperService);

    hasStats(event: EventModel): boolean {
        if (!event) {
            return false;
        }

        const isLiveStatsAvailable = event.scoreboard.started && event.scoreboard.statistics;
        const isPreMatchStatsAvailable = event.hasFixtureStats || event.hasLeagueStats;
        const areParticipantsAvailable = event.participants && event.participants.length;
        const isLeagueTableOnlyAvailable = !event.hasFixtureStats && event.hasLeagueStats;

        return !!(
            (this.sportTypes[event.sport.id] || this.nativeApp.isTerminal) &&
            (isLeagueTableOnlyAvailable || areParticipantsAvailable) &&
            (isLiveStatsAvailable || isPreMatchStatsAvailable)
        );
    }

    hasAnimation(event: EventModel): boolean {
        const isEligible = this.visualizationEligible(event);

        const hasAnimation = isEligible && this.animationProvider.sportAnimations[event.sport.id];
        const hasBetRadarVisualization = isEligible && this.hasBetRadarVisualization(event, false);
        const hasImgAnimation = this.hasImgAnimationVisualization(event);
        const hasPerformMedia = this.hasPerformMediaProvider(event, StreamType.FastData);

        return hasAnimation || hasBetRadarVisualization || hasImgAnimation || hasPerformMedia;
    }

    hideCommentaryFeed(event: EventModel): boolean {
        return !this.mediaConfig.showScoreboardCommentaryFeed && this.eventMediaHelperService.hasPerformPlayerEnabled(event);
    }

    hasPerformV2Visualization(event: EventModel): boolean {
        return this.hasPerformMediaProvider(event, StreamType.FastData);
    }

    getPerfomv2StreamType(event: EventModel): StreamType | undefined {
        if (this.mediaConfig.enableDefaultMediaTabByStreamType && this.eventMediaHelperService.hasPerformPlayerEnabled(event)) {
            return event?.stream?.streamType;
        }

        return undefined;
    }

    hasBetRadarMappingID(event: EventModel | null): boolean {
        return !!(event && event.statsId && event.sport.id === SportConstant.Soccer);
    }

    hasBetRadarVisualization(event: EventModel, checkVisualizationEligibility: boolean = true): boolean {
        if (checkVisualizationEligibility && !this.visualizationEligible(event)) {
            return false;
        }

        const coverage = this.animationProvider.betRadarSupportedCoverageLevel[event.sport.id];

        return !!(
            coverage !== undefined &&
            event.betRadarVisualizationId &&
            event.betRadarCoverageLevel &&
            event.betRadarCoverageLevel >= coverage &&
            this.animationProvider.betRadarSupportedAnimations[event.sport.id]
        );
    }

    hasImgAnimationVisualization(event: EventModel): boolean {
        const animationShowTime = DateProviderService.addHours(DateProviderService.getNow(), this.imgConfig.hoursToShowImgAnimation);
        const showPrematchAnimation =
            animationShowTime.getTime() >= event.startDate.getTime() && DateProviderService.getNow().getTime() <= event.startDate.getTime();

        return !!((showPrematchAnimation || event.live) && event.imgEventId);
    }

    hasBetRadarStickyAddonVisualization(event: EventModel): boolean {
        return this.hasBetRadarVisualization(event) && !!this.animationProvider.betRadarStickySupportedAnimations[event.sport.id];
    }

    getBackgroundColor(event: EventModel, useSolid: boolean): string | undefined {
        return getBackgroundColor(event, useSolid);
    }

    hasPerformMediaProvider(event: EventModel, streamType?: StreamType): boolean {
        if (event.stream?.provider !== StreamProvider.PerformV2) {
            return false;
        }

        return !streamType || streamType === event.stream.streamType;
    }

    private visualizationEligible(event: EventModel): boolean {
        return event.live && event.participants?.length > 0;
    }
}
